import { useMemo } from 'react';
import type { Scalars } from '@graphql/generated';
import { useWebsitePageListQuery } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { withWindow } from '@shared/utils/withWindow';

// Well-known page names at compile time (subset of EventPageSlugs enum values)
type WellKnownPageName =
  | 'accommodations'
  | 'custom'
  | 'faq'
  | 'guests'
  | 'registry'
  | 'registrylinks'
  | 'rsvp'
  | 'schedule'
  | 'settings'
  | 'story'
  | 'things-to-do'
  | 'travel'
  | 'video'
  | 'vip'
  | 'weddingparty'
  | 'welcome';

type WebsitePage = Readonly<{
  pageTitle: string;
  pageSlug: string;
  visibility: 'hidden' | 'passwordProtected' | 'public';
  id: string;
  isPageContainer: boolean;
  type: string;
  subTitle?: string | null;
  createdAt: Readonly<{
    timestamp: Scalars['Timestamp'];
  }>;
}>;

type UseWebsitePagesReturn = Readonly<{
  getPageByName: (name: WellKnownPageName) => WebsitePage | undefined;
  getPageBySlug: (slug: string) => WebsitePage | undefined;
  loading: boolean;
  pages: ReadonlyArray<WebsitePage>;
}>;

/**
 * Hook for O(1) lookup of website pages by slug.
 * Auto-detects admin vs guest mode by checking if pathname matches {handle}/edit pattern.
 * Creates a memoized Map for efficient page lookups.
 *
 * @example
 * const { getPageByName, getPageBySlug } = useWebsitePages();
 * const accommodationsPage = getPageByName('accommodations');
 * const customPage = getPageBySlug('my-custom-page');
 * const isHidden = !accommodationsPage || accommodationsPage.visibility === 'hidden';
 */
export const useWebsitePages = (): UseWebsitePagesReturn => {
  const { eventInfo, eventHandle, loading: loadingEventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId ?? '';

  // Check if pathname matches {handle}/edit (at end or followed by /)
  const isAdminRoute = withWindow(global => /\/[^/]+\/edit(?:\/|$)/.test(global.location.pathname), false);

  const { data, loading: loadingPages } = useWebsitePageListQuery({
    variables: { eventHandle: eventHandle ?? '', admin: isAdminRoute },
    batchMode: 'off',
    skip: !eventId,
  });

  // Create O(1) lookup map keyed by pageSlug
  const pagesBySlug = useMemo(() => {
    const pages = data?.eventByName?.pages ?? [];
    const map = new Map<string, WebsitePage>();
    pages.forEach(page => {
      if (page.pageSlug) {
        map.set(page.pageSlug, page);
      }
    });
    return map;
  }, [data]);

  const getPageByName = useMemo(() => (name: WellKnownPageName) => pagesBySlug.get(name), [pagesBySlug]);

  const getPageBySlug = useMemo(() => (slug: string) => pagesBySlug.get(slug), [pagesBySlug]);

  return {
    getPageByName,
    getPageBySlug,
    loading: loadingEventInfo || loadingPages,
    pages: data?.eventByName?.pages ?? [],
  };
};
